import {Row, Col, Table} from 'antd';
import {useEffect, useState} from 'react';
import {FaPlus} from 'react-icons/fa';
import {useIntl} from 'react-intl';
import {useParams} from 'react-router-dom';
import Button from '../../../../../../shared/components/button';
import {popupConfirm} from '../../../../../../shared/components/popup-confirm';
import {
  INDEX_PAGE_SIZE_DEFAULT,
  INDEX_PAGE_SIZE_OPTIONS,
} from '../../../../../../shared/constants/AppConst';
import {productAttributesService} from '../../../../../../shared/services/product-attribute';
import {PredefinedProductAttributeValuesDto} from '../../../../../../shared/services/product-attribute/dto/predefined-product-attribute-values-dto';
import {ProductAttributeDetailsDto} from '../../../../../../shared/services/product-attribute/dto/product-attribute-details-dto';
import ProductAttributeModal from '../product-attribute-modal';
import TableColumn from './columns';
import {useLocaleContext} from 'src/domain/utility/AppContextProvider/LocaleContextProvide';
import useTableV2 from 'src/shared/hooks/useTableV2';

interface IProductAttributesPredefinedValuesProps {
  productAttributesDetails?: ProductAttributeDetailsDto;
}

const ProductAttributesPredefinedValues = (
  props: IProductAttributesPredefinedValuesProps,
) => {
  const {productAttributesDetails} = props;
  const {messages} = useIntl();
  const {id} = useParams();
  const [predefinedValuesList, setPredefinedValuesList] = useState<
    PredefinedProductAttributeValuesDto[]
  >([]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [predefinedValueDetails, setPredefinedValueDetails] =
    useState<PredefinedProductAttributeValuesDto>();
  const [pageSizeOptions] = useState<string[]>(INDEX_PAGE_SIZE_OPTIONS);
  const [total, setTotal] = useState<number>(0);
  const [
    addPredefinedValuesModalVisbility,
    setAddPredefinedValuesModalVisbility,
  ] = useState(false);
  const [productAttributeType, setProductAttributeType] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: INDEX_PAGE_SIZE_DEFAULT,
  });
  const {locale}: any = useLocaleContext();
  const {showTotal, ...tableV2} = useTableV2();

  useEffect(() => {
    setProductAttributeType(productAttributesDetails?.Name ?? '');
  }, [productAttributesDetails]);

  useEffect(() => {
    if (id) {
      (async () => {
        try {
          setIsTableLoading(true);
          let predefinedValues =
            await productAttributesService.getPredefinedValues(id, {
              Page: pagination.page,
              PageSize: pagination.limit,
            });
          setPredefinedValuesList(predefinedValues.Data);
          setTotal(predefinedValues.Total);
        } catch (error) {
          console.error(error);
        } finally {
          setIsTableLoading(false);
        }
      })();
    }
  }, [id, pagination]);

  const getPredefinedValuesAll = async () => {
    if (id) {
      (async () => {
        try {
          setIsTableLoading(true);
          let predefinedValues =
            await productAttributesService.getPredefinedValues(id, {
              Page: pagination.page,
              PageSize: pagination.limit,
            });
          setPredefinedValuesList(predefinedValues.Data);
          setTotal(predefinedValues.Total);
        } catch (error) {
          console.error(error);
        } finally {
          setIsTableLoading(false);
        }
      })();
    }
  };

  const tablePagination = {
    total: total,
    pageSizeOptions: pageSizeOptions,
    pageSize: pagination.limit,
    showSizeChanger: true,
    showTotal,
    current: pagination.page,
    onShowSizeChange: async (current: any, PageSize: any) => {
      setPagination({limit: PageSize, page: current});
    },
    onChange: (page: any, pageSize: any) => {
      setPagination({limit: pageSize, page: page});
    },
  };

  const openPredefinedValueModal = (
    item: PredefinedProductAttributeValuesDto | undefined,
  ) => {
    setAddPredefinedValuesModalVisbility(true);
    setPredefinedValueDetails(item);
  };

  const onCancelModal = () => {
    setAddPredefinedValuesModalVisbility(false);
  };

  const onFinishModal = async () => {
    setAddPredefinedValuesModalVisbility(false);
    await getPredefinedValuesAll();
  };

  const deletePredefinedValues = (
    record: PredefinedProductAttributeValuesDto,
  ) => {
    popupConfirm(async () => {
      try {
        await productAttributesService.predefinedValueDelete(record.Id);
        await onFinishModal();
      } catch (error) {
        console.error(error);
      }
    }, `${messages['commom.areYouSureYouWantToDelete'].toString()}` + ' ' + [record?.Locales?.find((item) => item.LanguageId === locale.code)?.LocaleValue ?? record?.Name] + `?`);
  };

  const renderAddPredefinedValuesModal = () => {
    return (
      <ProductAttributeModal
        visible={addPredefinedValuesModalVisbility}
        predefinedValueDetails={predefinedValueDetails}
        productAttributeType={productAttributesDetails?.Name ?? ''}
        onCancel={onCancelModal}
        onFinish={onFinishModal}
      />
    );
  };

  return (
    <Row gutter={[0, 20]}>
      <Col span={24}>
        <div className='message-info'>
          {messages[
            'productAttributes.productAttributesPredefinedValues.message'
          ].toString()}
        </div>
      </Col>
      <Col span={24}>
        <Row justify='end'>
          <Col>
            <Button
              icon={<FaPlus className='add-button-icon' />}
              onClickAction={() => openPredefinedValueModal(undefined)}
              type='primary'
              text={messages[
                'productAttributes.productAttributesPredefinedValues.addNewValues'
              ].toString()}
              size='small'
              className='add-button'
            />
          </Col>
        </Row>
      </Col>
      <Col span={24}>
        <Table
          columns={TableColumn(
            productAttributeType,
            openPredefinedValueModal,
            deletePredefinedValues,
          )}
          rowKey={(record: PredefinedProductAttributeValuesDto) =>
            record.Id?.toString()
          }
          dataSource={predefinedValuesList}
          pagination={tablePagination}
          bordered={true}
          loading={isTableLoading}
          {...tableV2}
        />
      </Col>
      {renderAddPredefinedValuesModal()}
    </Row>
  );
};

export default ProductAttributesPredefinedValues;
